/**
 * Tenant attachment hooks (REQ-AGT-003 enmienda 14-Oct-2026).
 *
 * Own knowledge/tools attachments on (also) `use_only` agents. These endpoints
 * never read nor modify the publisher base — the tenant only ever sees what
 * it attached itself.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { getErrorMessage } from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types (mirror api/src/agents/schemas.py — Tenant*Overlay)
// =============================================================================

export interface TenantKnowledgeEntry {
  name: string;
  priority: number;
}

export interface TenantKnowledgeOverlay {
  collections: TenantKnowledgeEntry[];
}

export interface TenantToolEntry {
  server: string;
  operations: string[];
}

export interface TenantToolsOverlay {
  servers: TenantToolEntry[];
}

// =============================================================================
// Query keys
// =============================================================================

const overlayKeys = {
  knowledge: (tenantId: string | undefined, agentId: string | undefined) =>
    ['agents', tenantId, agentId, 'tenant-knowledge'] as const,
  tools: (tenantId: string | undefined, agentId: string | undefined) =>
    ['agents', tenantId, agentId, 'tenant-tools'] as const,
};

function agentPath(tenantId: string, agentId: string): string {
  return `/api/v1/tenants/${tenantId}/agents/${agentId}`;
}

// =============================================================================
// Knowledge overlay
// =============================================================================

export function useTenantKnowledge(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: overlayKeys.knowledge(tenantId, agentId),
    queryFn: async (): Promise<TenantKnowledgeOverlay> => {
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      const { data } = await apiClient.get<TenantKnowledgeOverlay>(
        `${agentPath(tenantId, agentId)}/tenant-knowledge`,
      );
      return data;
    },
    enabled: !!agentId && !!tenantId,
  });
}

export function useAttachTenantKnowledge(agentId: string | undefined) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (payload: { collection_ids: string[]; priority?: number }) => {
      const tenantId = currentTenant?.id;
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      const { data } = await apiClient.post<TenantKnowledgeOverlay>(
        `${agentPath(tenantId, agentId)}/tenant-knowledge`,
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(overlayKeys.knowledge(currentTenant?.id, agentId), data);
    },
    onError: (err) => getErrorMessage(err),
  });
}

export function useDetachTenantKnowledge(agentId: string | undefined) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (ref: string) => {
      const tenantId = currentTenant?.id;
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      await apiClient.delete(
        `${agentPath(tenantId, agentId)}/tenant-knowledge/${encodeURIComponent(ref)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: overlayKeys.knowledge(currentTenant?.id, agentId),
      });
    },
  });
}

// =============================================================================
// Tools overlay
// =============================================================================

export function useTenantTools(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: overlayKeys.tools(tenantId, agentId),
    queryFn: async (): Promise<TenantToolsOverlay> => {
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      const { data } = await apiClient.get<TenantToolsOverlay>(
        `${agentPath(tenantId, agentId)}/tenant-tools`,
      );
      return data;
    },
    enabled: !!agentId && !!tenantId,
  });
}

export function useAttachTenantTools(agentId: string | undefined) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (payload: {
      servers: { server: string; operations: string[] }[];
    }) => {
      const tenantId = currentTenant?.id;
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      const { data } = await apiClient.post<TenantToolsOverlay>(
        `${agentPath(tenantId, agentId)}/tenant-tools`,
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(overlayKeys.tools(currentTenant?.id, agentId), data);
    },
  });
}

export function useDetachTenantTools(agentId: string | undefined) {
  const qc = useQueryClient();
  const { currentTenant } = useTenant();
  return useMutation({
    mutationFn: async (server: string) => {
      const tenantId = currentTenant?.id;
      if (!tenantId || !agentId) throw new Error('No tenant/agent selected');
      await apiClient.delete(
        `${agentPath(tenantId, agentId)}/tenant-tools/${encodeURIComponent(server)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: overlayKeys.tools(currentTenant?.id, agentId),
      });
    },
  });
}
